import { create } from "zustand";
import type { ToolsError, WorkspaceToolsState } from "../../model/types";
import { DISABLED_STATE } from "../api/workspaceToolsApi";

export type LoadingKind = "load" | "reload";

type WorkspaceToolsStore = {
	workspace: string | null;
	state: WorkspaceToolsState;
	revision: number;
	loadingCount: number;
	reloadingCount: number;
	reloadConfirm: { unpublished: number | null } | null;
	dismissedErrorSeq: number | null;
	refusal: ToolsError | null;
	updateFailureToldIn: ReadonlySet<string | null>;
	switchWorkspace: (workspace: string | null) => void;
	setState: (workspace: string | null, state: WorkspaceToolsState) => void;
	startLoading: (kind?: LoadingKind) => () => void;
	setReloadConfirm: (reloadConfirm: { unpublished: number | null } | null) => void;
	dismissError: (seq: number) => void;
	showRefusal: (refusal: ToolsError) => void;
	dismissRefusal: () => void;
	showErrorAgain: () => void;
	markUpdateFailureTold: () => void;
};

const initial = {
	state: DISABLED_STATE,
	loadingCount: 0,
	reloadingCount: 0,
	reloadConfirm: null,
	dismissedErrorSeq: null,
	refusal: null,
};

export const useWorkspaceToolsStore = create<WorkspaceToolsStore>((set, get) => ({
	workspace: null,
	revision: 0,
	updateFailureToldIn: new Set(),
	...initial,
	switchWorkspace: (workspace) => {
		if (get().workspace === workspace) return;
		set((store) => ({ ...initial, workspace, revision: store.revision + 1 }));
	},
	setState: (workspace, state) => {
		if (get().workspace !== workspace) return;
		set((store) => ({ state, revision: store.revision + 1 }));
	},
	startLoading: (kind = "load") => {
		const { workspace } = get();
		const reloads = kind === "reload" ? 1 : 0;
		set((store) => ({ loadingCount: store.loadingCount + 1, reloadingCount: store.reloadingCount + reloads }));
		return () => {
			if (get().workspace !== workspace) return;
			set((store) => ({
				loadingCount: Math.max(0, store.loadingCount - 1),
				reloadingCount: Math.max(0, store.reloadingCount - reloads),
			}));
		};
	},
	setReloadConfirm: (reloadConfirm) => set({ reloadConfirm }),
	dismissError: (seq) => set({ dismissedErrorSeq: seq }),
	showRefusal: (refusal) => set({ refusal }),
	dismissRefusal: () => set({ refusal: null }),
	showErrorAgain: () => set({ dismissedErrorSeq: null }),
	markUpdateFailureTold: () =>
		set((store) => ({ updateFailureToldIn: new Set(store.updateFailureToldIn).add(store.workspace) })),
}));

export const useEnabledToolsState = () => useWorkspaceToolsStore((store) => (store.state.enabled ? store.state : null));

export const useToolsEnabled = () => useWorkspaceToolsStore((store) => store.state.enabled);

export const useToolsLoading = () => useWorkspaceToolsStore((store) => store.loadingCount > 0);

/** A reload alone checks the connection before anything loads; a clone under way loads whatever else runs. */
export const useOnlyReloading = () =>
	useWorkspaceToolsStore((store) => store.reloadingCount > 0 && store.reloadingCount === store.loadingCount);
